import { useMemo } from 'react'
import { Mic } from 'lucide-react'
import { MeetingIcon } from '@renderer/chat'
import type { MeetingAppName } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import { useCalendars, type Calendar } from '@renderer/hooks/use-calendars'
import { KNOWN_MEETING_APPS, formatTimeUntil } from '../../../utils/activity-helpers'
import { useJoinMeeting } from '../../../components/meeting/use-join-meeting'
import { MeetingBriefGenerationPrompt, useCanGenerateBrief, useMeetingBriefAllowance } from './brief-generation'
import { LockedBrief } from './locked-brief'
import { MeetingJoinButton } from './meeting-join-button'
import type { MeetingViewModel } from '../use-meeting-view-model'

const GLOW_ICON_SIZE = 220
const HERO_ICON_SIZE = 64

/** Mirrors the icon resolution in `header.tsx` at whatever size the hero needs. */
function resolveMeetingIcon(model: MeetingViewModel, calendars: Array<Calendar> | undefined, size: number) {
  if (model.calendarEvent) {
    return <CalendarEventIconView event={model.calendarEvent} size={size} calendars={calendars} />
  }
  const appName = model.audioNote?.meeting?.app?.name
  if (appName && KNOWN_MEETING_APPS.includes(appName)) {
    return <MeetingIcon appName={appName as MeetingAppName} size={size} />
  }
  return <Mic className="text-foreground-secondary" size={size} />
}

interface MeetingViewGetReadyProps {
  model: MeetingViewModel
}

/**
 * Pre-meeting hero (HIG-5827 follow-up): what the body renders instead of an
 * empty stack while the meeting hasn't started and no brief exists yet. Fills
 * the body so the Join CTA and Prepare Brief prompt read as the main event
 * rather than a header afterthought — see `use-meeting-brief-body`'s
 * `'get-ready'` presentation.
 */
export function MeetingViewGetReady({ model }: MeetingViewGetReadyProps) {
  const { calendarEvent, title, startedAt, endedAt } = model
  const { calendars } = useCalendars()
  const { joinLink, joinLabel, isAwaitingJoin, handleJoinClick } = useJoinMeeting(
    calendarEvent,
    'sidepanel meeting view get ready',
  )
  // Mirrors header.tsx's showJoinButton: joinable until the scheduled end
  // passes. `isUpcomingWithoutAudioNote` already rules out live/recorded.
  const isMeetingOver = !!endedAt && endedAt.getTime() < Date.now()
  const showJoinButton = !!joinLink && !isMeetingOver

  const icon = useMemo(() => resolveMeetingIcon(model, calendars, HERO_ICON_SIZE), [model, calendars])
  const glowIcon = useMemo(() => resolveMeetingIcon(model, calendars, GLOW_ICON_SIZE), [model, calendars])

  const relativeTime = startedAt ? formatTimeUntil(startedAt, endedAt ?? undefined) : null
  const canGenerateBrief = useCanGenerateBrief(model)
  const briefs = useMeetingBriefAllowance()

  // Free's briefs are counted, so the brief waits blurred for the user to spend one on this meeting.
  if (canGenerateBrief && briefs && !model.briefLoading) {
    const join = showJoinButton ? (
      <MeetingJoinButton joinLabel={joinLabel} isAwaitingJoin={isAwaitingJoin} onClick={handleJoinClick} />
    ) : undefined
    return <LockedBrief model={model} briefs={briefs} top={join} />
  }

  return (
    <div className="flex flex-1 flex-col items-center px-5 py-6">
      {/* 45/55 spacers sit the hero slightly above dead center. */}
      <div aria-hidden="true" className="flex-[0.45]" />
      <div className="flex flex-col items-center gap-6">
        <div className="relative flex size-16 items-center justify-center">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-25 blur-[64px]"
            style={{ width: GLOW_ICON_SIZE, height: GLOW_ICON_SIZE }}
          >
            {glowIcon}
          </div>
          <div className="relative">{icon}</div>
        </div>

        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-foreground type-ui-sm font-semibold">Get ready for</p>
          <h2 className="text-foreground type-ui-md line-clamp-2 break-words font-semibold">{title}</h2>
          {relativeTime && <p className="text-foreground-secondary type-ui-sm">{relativeTime}</p>}
        </div>

        <div className="flex w-full max-w-[250px] flex-col gap-2.5">
          {showJoinButton && (
            <MeetingJoinButton joinLabel={joinLabel} isAwaitingJoin={isAwaitingJoin} onClick={handleJoinClick} />
          )}
          <MeetingBriefGenerationPrompt model={model} />
        </div>
      </div>
      <div aria-hidden="true" className="flex-[0.55]" />
    </div>
  )
}
